import React, { useState, useEffect, useRef } from 'react';
import { FaShareAlt, FaEnvelope } from 'react-icons/fa';
import API_URL from '../api/APIUrl';
import { Link } from 'react-router-dom';
import html2pdf from 'html2pdf.js';

const StudentSearch = () => {
  const PDF_LOGO = "images/ahct-logo.png";
  const PDF_ORG_NAME = "Al Haidery Charitable Trust";
  const PDF_PRESIDENT = "Qutubuddin Shabbir Sutarwala";
  const PDF_PHONE = "+91 82650 23256";
  const PDF_EMAIL = "info@alhaidery.org";

  const resultsRef = useRef(null);

  const [dashboard, setDashboard] = useState({
    totalStudents: 3,
    maleStudents: 3,
    femaleStudents: 0,
    totalFees: 5000,
    arrangedAmount: 3000,
    balanceAmount: 2000,
    religionStats: []
  });

  const [filters, setFilters] = useState({
    state: '',
    city: '',
    zipcode: '',
    educationCategory: '',
    religion: '',
    caste: '',
    subCaste: ''
  });

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Dropdown Data
  const [allStates, setAllStates] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [allReligions, setAllReligions] = useState([]);
  const [allCastes, setAllCastes] = useState([]);
  const [allSubCastes, setAllSubCastes] = useState([]);

  const [showPopup, setShowPopup] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const handleContactAdmin = (student) => {
    setSelectedStudent(student);
    setShowPopup(true);
  };

  // Convert an image URL to a base64 data URL so jsPDF can draw it in the header.
  const toDataURL = (url) =>
    fetch(url, { mode: 'cors' })
      .then((res) => res.blob())
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      )
      .catch((err) => {
        console.warn('Could not load logo for PDF header:', err);
        return null;
      });

  const exportToPDF = async () => {
    const element = resultsRef.current;
    if (!element) return;

    const buttons = element.querySelectorAll(".no-pdf");
    buttons.forEach((button) => {
      button.style.display = "none";
    });

    setExporting(true);

    const opt = {
      margin: [24, 8, 16, 8], // top, left, bottom, right (mm) - room for header/footer
      filename: 'student-search-results.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'], avoid: '.compact-card' }
    };

    try {
      const logoDataUrl = await toDataURL(PDF_LOGO);

      const worker = html2pdf().set(opt).from(element).toPdf();
      const pdf = await worker.get('pdf');

      const totalPages = pdf.internal.getNumberOfPages();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);

        // ---- Header ----
        if (logoDataUrl) {
          try {
            pdf.addImage(logoDataUrl, 'PNG', 8, 4, 14, 14);
          } catch (e) {
            console.warn('Logo could not be drawn on PDF:', e);
          }
        }

        pdf.setFontSize(12);
        pdf.setTextColor(30, 30, 30);
        pdf.text(PDF_ORG_NAME, 26, 10);

        pdf.setFontSize(8);
        pdf.setTextColor(110, 110, 110);
        pdf.text(`President: ${PDF_PRESIDENT}`, 26, 15);

        pdf.setDrawColor(200, 200, 200);
        pdf.line(8, 20, pageWidth - 8, 20);

        // ---- Footer ----
        pdf.setDrawColor(200, 200, 200);
        pdf.line(8, pageHeight - 12, pageWidth - 8, pageHeight - 12);

        pdf.setFontSize(8);
        pdf.setTextColor(90, 90, 90);
        pdf.text(`Phone: ${PDF_PHONE}   |   Email: ${PDF_EMAIL}`, 8, pageHeight - 6);

        pdf.setFontSize(8);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 28, pageHeight - 6);
      }

      await worker.save();
    } catch (error) {
      console.error("PDF generation failed:", error);
    } finally {
      buttons.forEach((button) => {
        button.style.display = "";
      });
      setExporting(false);
    }
  };

  useEffect(() => {
    const fetchAllStates = async () => {
      const response = await fetch(API_URL.GET_ALL_STATES);
      const json = await response.json();
      setAllStates(response.ok ? json : []);
    };
    fetchAllStates();
  }, []);

  useEffect(() => {
    const fetchAllCities = async () => {
      if (filters.state) {
        const response = await fetch(API_URL.GET_ALL_CITIES + filters.state);
        const json = await response.json();
        setAllCities(response.ok ? json : []);
      } else {
        setAllCities([]);
      }
    };
    setFilters((prev) => ({ ...prev, city: '' }));
    fetchAllCities();
  }, [filters.state]);

  useEffect(() => {
    const fetchAllReligions = async () => {
      const response = await fetch(API_URL.GET_ALL_RELIGION);
      const json = await response.json();
      setAllReligions(response.ok ? json : []);
    };
    fetchAllReligions();
  }, []);

  useEffect(() => {
    const fetchAllCastes = async () => {
      if (filters.religion) {
        const response = await fetch(API_URL.GET_CASTE_BY_RELIGION + filters.religion);
        const json = await response.json();
        setAllCastes(response.ok ? json : []);
      } else {
        setAllCastes([]);
      }
    };
    setFilters((prev) => ({ ...prev, caste: '', subCaste: '' }));
    fetchAllCastes();
  }, [filters.religion]);

  useEffect(() => {
    const fetchAllSubCastes = async () => {
      if (filters.caste) {
        const response = await fetch(API_URL.GET_SUBCASTE_BY_CASTE + filters.caste);
        const json = await response.json();
        setAllSubCastes(response.ok ? json : []);
      } else {
        setAllSubCastes([]);
      }
    };
    setFilters((prev) => ({ ...prev, subCaste: '' }));
    fetchAllSubCastes();
  }, [filters.caste]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL.SEARCH_STUDENT}?state=${filters.state}&city=${filters.city}&zipcode=${filters.zipcode}&educationCategory=${filters.educationCategory}&religion=${filters.religion}&caste=${filters.caste}&subCaste=${filters.subCaste}`);
      const data = await response.json();
      setStudents(data.success && Array.isArray(data.students) ? data.students : []);
      setError(data.success ? null : 'No students found');
    } catch (err) {
      setError('Failed to fetch students');
      setStudents([]);
    }
    setLoading(false);
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleShare = (student) => {
    const balanceFees = calculateBalance(student.total_fees, student.arranged_amount);
    const formattedName = formatName(student.firstname, student.middlename, student.surname);

    const studentDetails = `Student: ${formattedName}
State: ${student.state}
City: ${student.city}
Religion: ${student.religion}
Caste: ${student.caste}
Sub-Caste: ${student.subCaste}
Education: ${student.education_category}
Needs Help for: ${student.need_financial_help_for}
Total Fees: ${student.total_fees}
Arranged: ${student.arranged_amount}
Balance Fees: ${balanceFees}`;

    navigator.share({
      text: studentDetails,
      url: `${API_URL.GET_BASE}student/${student._id}`
    });
  };

  const formatName = (firstname, middlename = '', surname = '') => {
    const middleInitial = middlename ? middlename.charAt(0).toUpperCase() + '.' : '';
    const lastInitial = surname ? surname.charAt(0).toUpperCase() + '.' : '';
    return `${firstname} ${middleInitial} ${lastInitial}`.trim();
  };

  const calculateBalance = (total, arranged) => {
    const totalAmt = Number(total) || 0;
    const arrangedAmt = Number(arranged) || 0;
    return totalAmt - arrangedAmt;
  };

  // Extracts just the numeric portion from a full reg code, e.g.
  // "10/24-S0007-MAH-F-PUN" -> "0007"
  const extractRegNumber = (code) => {
    if (!code) return "-";
    const match = code.match(/\d{4,}/);
    return match ? match[0] : code;
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "-";
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="container mt-4">
      {/* Compact styles - shared between screen display and PDF export */}
      <style>{`
        .student-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 14px;
          background:#fff;
              padding: 10px;
        }
        @media (max-width: 768px) {
          .student-grid {
            grid-template-columns: 1fr;
          }
        }
        .compact-card {
          font-size: 0.9rem;
          line-height: 1.8;
          padding: 10px 14px !important;
          border: 1px solid #e3e3e3 !important;
          border-radius: 8px;
        }
        .compact-card h6 {
          font-size: 0.95rem;
          margin-bottom: 0;
        }
        .compact-card .badge {
          font-size: 0.65rem;
          padding: 2px 6px;
        }
        .compact-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2px;
        }
        .compact-fields {
          display: flex;
          flex-wrap: wrap;
          gap: 3px 16px;
          margin-top: 2px;
        }
        .compact-fields span {
          white-space: nowrap;
        }
        .compact-fields .label {
          color: #888;
          margin-right: 3px;
        }

        .balance-due-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 6px;
          padding-top: 4px;
          border-top: 1px dashed #ddd;
        }
        .balance-due-row .due-date {
          font-weight: 600;
          color: #333;
        }
        .balance-due-row .due-date .label {
          color: #888;
          font-weight: normal;
          margin-right: 3px;
        }
      `}</style>

      <h2>Search for Students</h2>
      <div className="row mb-4">

        <div className="col-md-2">
          <div className="card shadow border-0 bg-primary text-white">
            <div className="card-body text-center">
              <h6>Total Students</h6>
              <h5>{dashboard.totalStudents}</h5>
            </div>
          </div>
        </div>

        <div className="col-md-2">
          <div className="card shadow border-0 bg-success text-white">
            <div className="card-body text-center">
              <h6>Male</h6>
              <h5>{dashboard.maleStudents}</h5>
            </div>
          </div>
        </div>

        <div className="col-md-2">
          <div className="card shadow border-0 bg-info text-white">
            <div className="card-body text-center">
              <h6>Female</h6>
              <h5>{dashboard.femaleStudents}</h5>
            </div>
          </div>
        </div>

        <div className="col-md-2">
          <div className="card shadow border-0 bg-warning text-dark">
            <div className="card-body text-center">
              <h6>Total Fees</h6>
              <h5>₹ {dashboard.totalFees.toLocaleString()}</h5>
            </div>
          </div>
        </div>

        <div className="col-md-2">
          <div className="card shadow border-0 bg-secondary text-white">
            <div className="card-body text-center">
              <h6>Arranged</h6>
              <h5>₹ {dashboard.arrangedAmount.toLocaleString()}</h5>
            </div>
          </div>
        </div>

        <div className="col-md-2">
          <div className="card shadow border-0 bg-danger text-white">
            <div className="card-body text-center">
              <h6>Balance</h6>
              <h5>₹ {dashboard.balanceAmount.toLocaleString()}</h5>
            </div>
          </div>
        </div>

      </div>

      <div className="row mb-4">
        {dashboard.religionStats.map((religion) => (
          <div className="col-md-2 mb-3" key={religion._id}>
            <div className="card shadow-sm border-left-primary">
              <div className="card-body text-center">
                <h6>{religion._id}</h6>
                <h3>{religion.count}</h3>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row mb-3">
        <div className="col-md-3">
          <select name="state" value={filters.state} onChange={handleFilterChange} className="form-control">
            <option value="">Select State</option>
            {allStates.map((state) => <option key={state._id} value={state._id}>{state.title}</option>)}
          </select>
        </div>
        <div className="col-md-3">
          <select name="city" value={filters.city} onChange={handleFilterChange} className="form-control" disabled={!filters.state}>
            <option value="">Select City</option>
            {allCities.map((city) => <option key={city._id} value={city._id}>{city.title}</option>)}
          </select>
        </div>
        <div className="col-md-3">
          <select name="religion" value={filters.religion} onChange={handleFilterChange} className="form-control">
            <option value="">Select Religion</option>
            {allReligions.map((religion) => <option key={religion._id} value={religion.title}>{religion.title}</option>)}
          </select>
        </div>
        {/* <div className="col-md-3">
          <select name="caste" value={filters.caste} onChange={handleFilterChange} className="form-control" disabled={!filters.religion}>
            <option value="">Select Caste</option>
            {allCastes.map((caste) => <option key={caste._id} value={caste.title}>{caste.title}</option>)}
          </select>
        </div> */}
        <div className="col-md-3">
          <select name="educationCategory" value={filters.educationCategory} onChange={handleFilterChange} className="form-control">
            <option value="">Select</option>
            <option value="Pre-Primary (LKG to UKG)">Pre-Primary (LKG to UKG)</option>
            <option value="Primary (1st to 4th)">Primary (1st to 4th)</option>
            <option value="High School (5th to 10th)">High School (5th to 10th)</option>
            <option value="Junior College (11th to 12th)">Junior College (11th to 12th)</option>
            <option value="Diploma Course">Diploma Course</option>
            <option value="Graduation">Graduation</option>
            <option value="Post Graduation">Post Graduation</option>
            <option value="Professional Course">Professional Course</option>
            <option value="Administrative Course">Administrative Course</option>
            <option value="Educational Equipment ">Educational Equipment</option>
          </select>
        </div>
      </div>
      <div className="row mb-3">
        {/* <div className="col-md-3">
          <select name="subCaste" value={filters.subCaste} onChange={handleFilterChange} className="form-control" disabled={!filters.caste}>
            <option value="">Select Sub-Caste</option>
            {allSubCastes.map((subCaste) => <option key={subCaste._id} value={subCaste.title}>{subCaste.title}</option>)}
          </select>
        </div> */}
        
        <div className="col-md-3">
          <button className="btn btn-primary" onClick={fetchStudents}>Search</button>
        </div>
      </div>
      {loading && <p>Loading...</p>}
      {error && <p className="text-danger">{error}</p>}
      {students.length === 0 && !loading && <p>No students found matching your filters.</p>}

      <div className="d-flex">
        <button
          className="btn btn-success ms-2"
          onClick={exportToPDF}
          disabled={exporting || students.length === 0}
        >
          {exporting ? 'Generating PDF...' : 'Export PDF'}
        </button>
      </div>

      <div ref={resultsRef} className="student-grid">

        {students.map((student) => (
          <div
            key={student.firstname + student.surname}
            className="compact-card"
          >
            <div className="compact-header-row">
              <div className="text-muted small fw-semibold">
                REG. NO: {extractRegNumber(student.code)}
              </div>

              <Link
                to={`/student/${student._id}`}
                className="btn btn-primary btn-sm py-0 px-2 no-pdf"
              >
                View Details
              </Link>
            </div>

            <div className="d-flex align-items-center gap-2">
              <h6 className="fw-semibold mb-0">
                {formatName(student.firstname, student.middlename, student.surname)}
              </h6>

              <span
                className={`badge ${
                  student.gender?.toLowerCase() === "male"
                    ? "bg-primary"
                    : student.gender?.toLowerCase() === "female"
                    ? "bg-danger"
                    : "bg-secondary"
                }`}
              >
                {student.gender?.toLowerCase() === "male"
                  ? "♂ Male"
                  : student.gender?.toLowerCase() === "female"
                  ? "♀ Female"
                  : `⚧ ${student.gender || "Other"}`}
              </span>
            </div>

            <div className="compact-fields">
              <span><span className="label">State/City:</span>{student.state || "-"} / {student.city || "-"}</span>
              <span><span className="label">Education:</span>{student.education_category || "-"}</span>
              <span><span className="label">Help For:</span>{student.need_financial_help_for || "-"}</span>
              <span><span className="label">Religion:</span>{student.religion || "-"}</span>
              {/* <span><span className="label">Religion/Caste:</span>{student.religion || "-"} / {student.caste || "-"} / {student.subCaste || "-"}</span> */}
              <span><span className="label">Total Fees:</span>₹{student.total_fees || 0}</span>
              <span><span className="label">Arranged:</span>₹{student.arranged_amount || 0}</span>
              
            </div>

            <div className="balance-due-row">
              <span className="text-danger fw-semibold">
                <span className="label" style={{ color: '#dc3545' }}>Balance:</span>
                ₹{calculateBalance(student.total_fees, student.arranged_amount)}
              </span>
              <span className="due-date">
                <span className="label" style={{ color: '#dc3545' }}>Payment Due:</span>
                {formatDate(student.payment_deadline)}
              </span>
            </div>
          </div>
        ))}

        {showPopup && selectedStudent && (
          <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Contact Admin</h5>
                  <button type="button" className="close" onClick={() => setShowPopup(false)}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <p>You can contact the admin to help this student:</p>
                  <p><strong>Name:</strong> {formatName(selectedStudent.firstname, selectedStudent.middlename, selectedStudent.surname)}</p>
                  <p><strong>Email:</strong> <a href={`mailto:${PDF_EMAIL}`}>{PDF_EMAIL}</a></p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowPopup(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentSearch;
